import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { COLORS } from "@/data/colors";
import { Round as QuizRound } from "@/data/quiz";
import { useColorPulse } from "@/context/color-pulse";

interface QuizFlowProps {
  rounds: QuizRound[];
  onComplete: (choices: string[]) => void;
}

export function QuizFlow({ rounds, onComplete }: QuizFlowProps) {
  const [currentRoundIndex, setCurrentRoundIndex] = useState(0);
  const [choices, setChoices] = useState<string[]>([]);
  const [selectedInRound, setSelectedInRound] = useState<string | null>(null);
  const { trigger, setOptionColors } = useColorPulse();

  const currentRound = rounds[currentRoundIndex];

  // While a round's options are on screen, feed their colors into the
  // ambient background so it mixes/drifts through the choices being
  // considered. Cleared on unmount so other pages stay plain.
  useEffect(() => {
    if (currentRound) {
      setOptionColors(currentRound.options.map((id) => COLORS[id].hex));
    }
    return () => setOptionColors([]);
  }, [currentRound?.id]);

  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
    };
  }, []);

  const handleSelect = (colorId: string) => {
    if (selectedInRound) return; // prevent double clicks
    
    setSelectedInRound(colorId);
    trigger(COLORS[colorId].hex);
    
    advanceTimer.current = setTimeout(() => {
      const newChoices = [...choices, colorId];
      if (currentRoundIndex < rounds.length - 1) {
        setChoices(newChoices);
        setCurrentRoundIndex(prev => prev + 1);
        setSelectedInRound(null);
      } else {
        onComplete(newChoices);
      }
    }, 800); // Wait for animation
  };

  if (!currentRound) return null;

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 w-full max-w-4xl mx-auto">
      <div className="w-full mb-12 flex items-center justify-between text-sm font-medium text-muted-foreground">
        <span>Round {currentRoundIndex + 1} of {rounds.length}</span>
        <div className="flex gap-1">
          {rounds.map((_, i) => (
            <div 
              key={i} 
              className={`w-8 h-1 rounded-full transition-colors duration-500 ${i <= currentRoundIndex ? 'bg-primary' : 'bg-border'}`}
            />
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentRound.id}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.05 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="w-full flex flex-col items-center"
        >
          <h2 className="text-2xl md:text-4xl font-serif text-center mb-12 max-w-2xl leading-tight">
            {currentRound.prompt}
          </h2>

          <div className="flex flex-wrap justify-center gap-4 md:gap-8 w-full">
            {currentRound.options.map((colorId) => {
              const colorData = COLORS[colorId];
              const isSelected = selectedInRound === colorId;
              const isNotSelected = selectedInRound && selectedInRound !== colorId;

              return (
                <motion.button
                  key={colorId}
                  onClick={() => handleSelect(colorId)}
                  disabled={!!selectedInRound}
                  animate={{
                    scale: isSelected ? 1.1 : isNotSelected ? 0.9 : 1,
                    opacity: isNotSelected ? 0 : 1,
                  }}
                  whileHover={!selectedInRound ? { scale: 1.05 } : {}}
                  transition={{ duration: 0.4 }}
                  className={`w-32 h-32 md:w-48 md:h-48 rounded-full shadow-lg border-2 border-transparent hover:border-white/20 relative overflow-hidden focus:outline-none`}
                  style={{ backgroundColor: colorData.hex }}
                  aria-label={`Select ${colorData.name}`}
                >
                  <div className="absolute inset-0 bg-black/10 opacity-0 hover:opacity-100 transition-opacity" />
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
